import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import omitBy from 'lodash/omitBy';

export * from './array';

/**
 * Trims trailing and leading zeroes from a number string
 *
 * @param {number|string} value - the number to abbreviate
 * @return {string} - locale-formatted string without trailing or leading zeroes
 */
function trimZeroesNumber(value) {
  return parseFloat(value).toLocaleString();
}

// generic data-manipulation utils

/**
 * Turns a number into nicely formatted USD, e.g. 1,000 -> $1,000.00
 *
 * @param {number|string} dollarAmt - the number to turn into USD
 * @return {string} - number in USD
 */
export function numberInUSD(dollarAmt) {
  return dollarAmt.toLocaleString(`en-US`, {currency: `USD`, style: `currency`});
}

export function commaizeNumber(number, noConversion) {
  switch (typeof number) {
    case `number`:
      if (isNaN(number)) {
        return number;
      }
      number = number.toString();
      break;
    case `string`:
      // noop
      break;
    default:
      return number;
  }

  let neg = false;
  if (number[0] === `-`) {
    neg = true;
    number = number.slice(1);
  }

  // Parse main number
  const split = number.split(`.`);
  let commaized = noConversion ? split[0] : parseInt(split[0] || 0, 10).toString();

  if (commaized.length) {
    commaized = commaized
      .split(``)
      .reverse()
      .join(``);
    commaized = commaized.match(/.{1,3}/g).join(`,`);
    commaized = commaized
      .split(``)
      .reverse()
      .join(``);
  }

  if (split[1]) {
    // Add decimals, if applicable
    commaized += `.` + split[1];
  }

  if (neg) {
    commaized = `-` + commaized;
  }

  return commaized;
}

/**
 * Returns a numer e.g 12345 as an abbreviation e.g 123K
 * @param {number} number
 * @param {number} precision - decimal points e.g precision=2 returns 123.45K
 * @param {boolean} spaced - adds a space between suffix for readability e.g 123.45 K
 */
export function abbreviateNumber(number, precision = 1, spaced = false) {
  number = parseFloat(number);

  const largeNumbers = [
    [Math.pow(10, 15), `Q`],
    [Math.pow(10, 12), `T`],
    [Math.pow(10, 9), `B`],
    [Math.pow(10, 6), `M`],
    [Math.pow(10, 3), `K`],
  ];

  let suffix = ``;

  for (let i = 0; i < largeNumbers.length; i++) {
    const bignum = largeNumbers[i][0];
    const letter = largeNumbers[i][1];

    if (Math.abs(number) >= bignum) {
      number /= bignum;
      suffix = letter;
      break;
    }
  }

  const isNegative = number < 0;
  const fixed = number.toFixed(precision).split(`.`);
  let formatted = commaizeNumber(Math.abs(parseInt(fixed[0], 10)));

  if (fixed[1] && parseInt(fixed[1], 10) !== 0) {
    formatted += `.` + fixed[1];
  }

  return (isNegative ? `-` : ``) + formatted + (spaced ? ` ` : ``) + suffix;
}

export function abbreviateNumberNSigFigs(value, n, spaced = false) {
  const number = parseFloat(value);
  const scalar = Math.abs(number);

  if (scalar < 1) {
    const rounded = trimZeroesNumber(number.toFixed(n));
    const tooSmall = parseFloat(rounded) === 0 && number !== 0;
    return tooSmall ? `≈0` : rounded;
  } else {
    // initials of latin numerical words
    const suffixList = [
      [Math.pow(10, 15), `Q`],
      [Math.pow(10, 12), `T`],
      [Math.pow(10, 9), `B`],
      [Math.pow(10, 6), `M`],
      [Math.pow(10, 3), `K`],
      [Math.pow(10, 0), ``],
    ];

    const [factor, suffix] = suffixList.find(([factor]) => scalar >= factor);
    return trimZeroesNumber((number / factor).toPrecision(n)) + (spaced && suffix ? ` ` : ``) + suffix;
  }
}

export function abbreviateNumber4SigFigs(value, spaced = false) {
  return abbreviateNumberNSigFigs(value, 4, spaced);
}

/**
 * Abbreviates number with common latin numerical words and then rounds the abbreviated number to 3 significant figures
 *
 * Note: this method is hardcoded to 3 sig figs because the abbreviation suffix list goes up by 3 digit intervals
 * this ensures that the number will always be rounded to the next suffix without being abbreviated to an exponent
 * (eg. avoid 707K abbreviated to 7.1e+2 with 2 sig figs)
 *
 * Note: numbers smaller than 1 are rounded to 3 decimal places because significant figure rounding produces unintuitive strings in these cases
 *
 * @param {number|string} value - the number to abbreviate
 * @param {boolean} spaced - whether to add a space between the abbreviated number and the suffix
 * @return {string} - locale-formatted string abbreviation of value
 */
export function abbreviateNumber3SigFigs(value, spaced = false) {
  return abbreviateNumberNSigFigs(value, 3, spaced);
}

export function ordinalizeNumber(number) {
  const absNumber = Math.abs(number);
  // numbers ending in 1, 2, or 3 but not in 11, 12, or 13
  if (![11, 12, 13].includes(absNumber % 100) && [1, 2, 3].includes(absNumber % 10)) {
    switch (absNumber % 10) {
      case 1:
        return number + `st`;
      case 2:
        return number + `nd`;
      case 3:
        return number + `rd`;
      default:
        break;
    }
  }
  return number + `th`;
}

export function capitalize(string) {
  return string && string.charAt(0).toUpperCase() + string.slice(1);
}

export function extend() {
  return Object.assign(...[{}, ...Array.prototype.slice.call(arguments)]);
}

export function htmlEncodeString(val) {
  return String(val)
    .replace(/&/g, `&amp;`)
    .replace(/</g, `&lt;`)
    .replace(/>/g, `&gt;`)
    .replace(/"/g, `&quot;`)
    .replace(/'/g, `&#039;`)
    .replace(/\r/g, `&#013;`)
    .replace(/\n/g, `&#010;`);
}

// return object with new keys and same values
export function mapKeys(obj, func) {
  return Object.keys(obj).reduce((ret, k) => Object.assign(ret, {[func(k, obj[k])]: obj[k]}), {});
}

// return object with same keys and new values
export function mapValues(obj, func) {
  return Object.keys(obj).reduce((ret, k) => Object.assign(ret, {[k]: func(obj[k], k)}), {});
}

export function nestedObjectDepth(obj) {
  return obj !== null && typeof obj === `object` ? nestedObjectDepth(obj[Object.keys(obj)[0]]) + 1 : 0;
}

export function parseUrlQueryParams(urlSearch = window.location.search) {
  return urlSearch
    .slice(1)
    .split(`&`)
    .reduce((params, pairStr) => {
      const [k, v] = pairStr.split(`=`);
      return Object.assign(params, {[k]: v});
    }, {});
}

function getKeys(obj, depth, keySet) {
  if (nestedObjectDepth(obj) > depth) {
    Object.values(obj).forEach((value) => getKeys(value, depth, keySet));
  } else {
    Object.keys(obj).forEach((key) => keySet.add(key));
  }
}

export function nestedObjectKeys(obj, depth = 1) {
  const keys = new Set();
  getKeys(obj, depth, keys);
  return Array.from(keys);
}

export function objectFromPairs(pairs) {
  const object = {};
  pairs.forEach((pair) => {
    object[pair[0]] = pair[1];
  });
  return object;
}

export function objToQueryString(params) {
  return Object.keys(params)
    .map((k) => [k, encodeURIComponent(params[k])].join(`=`))
    .join(`&`);
}

export function parseUrl(url) {
  const parser = document.createElement(`a`);
  parser.href = url;
  return {
    host: parser.host,
    pathname: parser.pathname,
    url: parser.url,
  };
}

export function pluralize(singular, number, plural) {
  plural = plural || singular + `s`;
  return number === 0 || number > 1 ? plural : singular;
}

export function sum(arr) {
  let _sum = 0;
  for (let i = 0; i < arr.length; i++) {
    _sum += arr[i];
  }
  return _sum;
}

export function truncateMiddle(string, len) {
  if (string) {
    if (len <= 3) {
      return string.substr(0, len);
    } else if (string.length <= len) {
      return string;
    } else {
      const start = Math.ceil((len - 3) / 2);
      const end = -1 * Math.floor((len - 3) / 2);
      return string.substr(0, start) + `...` + (end ? string.substr(end) : ``);
    }
  }
  return string;
}

/**
 *  given a monotonically increasing function fn: integer => double,
 *  find the first integer i between start and end inclusive s.t. fn(i) >= 0
 *  or end if fn(end) < 0
 */
export function binarySearch(start, end, fn) {
  if (fn(start) >= 0) {
    return start;
  }
  if (fn(end) < 0) {
    return end;
  }
  while (end - start > 1) {
    const mid = Math.floor((end + start) / 2);
    if (fn(mid) < 0) {
      start = mid;
    } else {
      end = mid;
    }
  }
  return end;
}

// lazily initialize measureContext, rather than at module top-level,
// so that running tests from node does not cause an undefined
// variable error from document
let measureContext;
function getMeasureContext() {
  if (!measureContext) {
    measureContext = document.createElement(`canvas`).getContext(`2d`);
  }
  return measureContext;
}

/**
 * Returns false if `val` is undefined, null or NaN, otherwise true
 * NOTE: Returns true for 0 and '' (empty string)
 * @param val {any}
 * @returns {boolean}
 */
export function isDefinedValue(val) {
  if (typeof val === `number`) {
    return !isNaN(val);
  }
  return val !== undefined && val !== null;
}

/* istanbul ignore next */
export function measureTextWidth(string, font) {
  getMeasureContext().font = font;
  return getMeasureContext().measureText(string).width;
}

/* istanbul ignore next */
export function truncateToWidth(string, font, width) {
  getMeasureContext().font = font;
  // spaceLeft will give the amount of space left after truncating N chars off string
  const spaceLeft = (nChars) =>
    width - getMeasureContext().measureText(truncateMiddle(string, string.length - nChars)).width;
  // find the first N such that removing N chars allows the string to fit (spaceLeft is positive)
  const bestFit = binarySearch(0, string.length - 1, spaceLeft);
  return truncateMiddle(string, string.length - bestFit);
}

// Leading and repeated whitespace in strings will produce measurements that do not
// match the actual rendered width.
function normalizeString(str) {
  return str.replace(/\s+/g, ` `).trim();
}

/* istanbul ignore next */
export function truncateToElement(string, elem, {minWidth = 0} = {}) {
  let availableWidth = Math.max(minWidth, elem.clientWidth);
  const styles = window.getComputedStyle(elem);
  availableWidth -= parseInt(styles.getPropertyValue(`padding-left`));
  availableWidth -= parseInt(styles.getPropertyValue(`padding-right`));
  // Note: getPropertyValue('font') does not work in FF, so we construct a font property from its components
  const fontString = `font-style font-variant font-weight font-size font-family`
    .split(` `)
    .map((prop) => styles.getPropertyValue(prop))
    .join(` `);
  return truncateToWidth(normalizeString(string), fontString, availableWidth);
}

//get the rollingcount based on unit for calculating rolling average
export function defaultRollingWindowSize(unit) {
  switch (unit) {
    case `minute`:
      return 60;
    case `hour`:
      return 12;
    case `day`:
      return 7;
    case `week`:
      return 5;
    case `month`:
      return 3;
    case `quarter`:
      return 2;
  }
}

export function filterAttributes(attrs) {
  return omitBy(attrs, isNil);
}

export function serializeAttributes(attrs) {
  return mapValues(filterAttributes(attrs), (value) => (isObject(value) ? JSON.stringify(value) : value));
}

export function parseNumber(str) {
  if (Number.isFinite(str)) {
    return str;
  } else if (!str) {
    return null;
  } else {
    const num = Number(str);
    return Number.isNaN(num) ? null : num;
  }
}

/**
 * Generates a predictable anonymous string given
 * a number, e.g. 0 -> A, 1 -> B, 26 -> BA, 98 -> DU
 * 26 ^ 4 -> BAAAA
 * @param {number} index - Index of item to stringify
 */
export const getAlphaLabelFromIndex = (index) => {
  if (index < 0) return ``;

  const MIN_CHAR_CODE = `A`.charCodeAt(0);
  const MAX_ALPHA_INDEX = 26; // A-Z
  let str = ``;

  if (index === 0) {
    return `A`;
  }

  const depth = Math.floor(index / 26);
  const mod = index % MAX_ALPHA_INDEX;

  if (depth > 0) {
    str += getAlphaLabelFromIndex(depth);
  }

  return str + String.fromCharCode(MIN_CHAR_CODE + mod);
};

/**
 * Generates the index number the given alphabetic label
 * corresponds to, e.g. A -> 0, B -> 1, BA -> 26, DU -> 98
 * BAAAA -> 26 ^ 4
 * @param {string} alphaLabel - Label of the item to index
 */
export const getIndexFromAlphaLabel = (alphaLabel) => {
  if (alphaLabel === ``) {
    return -1;
  }

  const MIN_CHAR_CODE = `A`.charCodeAt(0);
  let index = 0;
  let currLetterValue;
  for (let i = 0; i < alphaLabel.length; i++) {
    currLetterValue = alphaLabel.charCodeAt(i) - MIN_CHAR_CODE;
    if (currLetterValue < 0 || currLetterValue > 25) {
      return -1;
    }
    index += currLetterValue * Math.pow(26, alphaLabel.length - 1 - i);
  }

  return index;
};

/**
 * Normalizes data group id comparison so that null, undefined and '' are equivalent.
 */
export function dataGroupIDsEqual(a, b) {
  return (!a && !b) || a === b;
}

// parseUri 1.2.2
// (c) Steven Levithan <stevenlevithan.com>
// MIT License
export function parseUri(str) {
  const o = parseUri.options,
    m = o.parser[o.strictMode ? `strict` : `loose`].exec(str),
    uri = {};
  let i = 14;

  while (i--) {
    uri[o.key[i]] = m[i] || ``;
  }

  uri[o.q.name] = {};
  uri[o.key[12]].replace(o.q.parser, function(_$0, $1, $2) {
    if ($1) {
      uri[o.q.name][$1] = $2;
    }
  });

  return uri;
}

parseUri.options = {
  strictMode: false,
  key: [
    `source`,
    `protocol`,
    `authority`,
    `userInfo`,
    `user`,
    `password`,
    `host`,
    `port`,
    `relative`,
    `path`,
    `directory`,
    `file`,
    `query`,
    `anchor`,
  ],
  q: {
    name: `queryKey`,
    parser: /(?:^|&)([^&=]*)=?([^&]*)/g,
  },
  parser: {
    strict: /^(?:([^:/?#]+):)?(?:\/\/((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:/?#]*)(?::(\d*))?))?((((?:[^?#/]*\/)*)([^?#]*))(?:\?([^#]*))?(?:#(.*))?)/,
    loose: /^(?:(?![^:@]+:[^:@/]*@)([^:/?#.]+):)?(?:\/\/)?((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:/?#]*)(?::(\d*))?)(((\/(?:[^?#](?![^?#/]*\.[^?#/.]+(?:[?#]|$)))*\/?)?([^?#/]*))(?:\?([^#]*))?(?:#(.*))?)/,
  },
};
